import React, { useMemo } from "react";
import {
  Box,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { describeEvent, formatEventValue } from "./pqrsdEventDescriptions";

import { pqrsdText } from "./pqrsdLabel";
// Admin/supervisor-only raw event log for a PQRSD. Renders the filed doc's
// `events[]` array (the authoritative audit trail written by the cloud
// functions) reverse-chronologically. Mirrors the labels used by
// RadicadoTimeline (mapEventTypeToSpanish) but stays self-contained so it can
// be opened as a lightweight slide-out without the full timeline fetch.
//
// Each entry leads with a sentence built from its own data (describeEvent), so
// ten emails no longer read as ten identical "Notificación enviada" rows; the
// chip keeps the category and the lines below keep the raw detail, readable.

const EVENT_LABEL = {
  FILED: "Radicado",
  ASSIGNED: "Asignado",
  RESPONSE_CREATED: "Respuesta creada",
  FORWARDED: "Reenviado",
  RESPONSE_FILED: "Respuesta radicada",
  FINALIZED: "Finalizado",
  RESPONSE_FLOW_STARTED: "Flujo de respuesta iniciado",
  RESPONSE_FLOW_STEP_DONE: "Paso de aprobación completado",
  RESPONSE_FLOW_REJECTED: "Aprobación devuelta",
  RESPONSE_FLOW_DONE: "Flujo de respuesta completado",
  PQRSD_ASSIGNED: "Asignada",
  PQRSD_RETURNED: "PQRSD devuelta",
  PQRSD_CANCELLED: "PQRSD anulada",
  PQRSD_FORWARDED: "PQRSD reenviada",
  PQRSD_REASSIGNED: "PQRSD reasignada",
  PQRSD_CITIZEN_CLOSED: "Cerrada por el ciudadano",
  PQRSD_CITIZEN_REOPENED: "Reabierta por el ciudadano",
  PQRSD_SLA_RESET: "Plazo (SLA) reiniciado",
  PQRSD_INFO_REQUESTED: "Requerimiento de información enviado",
  PQRSD_INFO_REPLIED: "Ciudadano completó el requerimiento",
  PQRSD_INFO_REQUEST_WITHDRAWN: "Requerimiento de información retirado",
  PQRSD_INFO_EXTENSION_GRANTED: "Prórroga concedida al ciudadano",
  PQRSD_INFO_EXPIRED: "Plazo del ciudadano vencido",
  PQRSD_SLA_PAUSED: "Plazo (SLA) suspendido",
  PQRSD_SLA_RESUMED: "Plazo (SLA) reanudado",
  PQRSD_DESISTIMIENTO_DECREED: "Desistimiento tácito decretado",
  PQRSD_APPEAL_FILED: "Recurso de reposición interpuesto",
  PQRSD_APPEAL_GRANTED: "Recurso concedido",
  PQRSD_APPEAL_DENIED: "Recurso negado",
  PQRSD_APPEAL_REMINDER_SENT: "Recordatorio del recurso",
  PQRSD_DESISTIMIENTO_FINAL: "Desistimiento en firme",
  PQRSD_RETURNED_TO_TRIAGE: "Devuelta a clasificación",
  RESPONSE_FILED_INTERIM: "Respuesta interina radicada",
  RESPONSE_FILED_FINAL: "Respuesta final radicada",
  EMAIL_SENT: "Notificación enviada",
  EMAIL_SKIPPED: "Correo no enviado",
  PQRSD_INTAKE_VENTANILLA: "Radicada por ventanilla",
  PQRSD_AI_CLASSIFIED: "Clasificada por IA",
  PQRSD_AI_ROUTED: "Enrutada a dependencia",
  PQRSD_AI_NEEDS_REVIEW: "Requiere clasificación manual",
};

const EVENT_COLOR = {
  PQRSD_ASSIGNED: "info",
  PQRSD_RETURNED: "error",
  PQRSD_CANCELLED: "error",
  RESPONSE_FLOW_REJECTED: "error",
  PQRSD_CITIZEN_REOPENED: "warning",
  PQRSD_SLA_RESET: "warning",
  PQRSD_INFO_REQUESTED: "warning",
  PQRSD_INFO_REPLIED: "success",
  PQRSD_INFO_EXPIRED: "error",
  PQRSD_SLA_PAUSED: "warning",
  PQRSD_SLA_RESUMED: "info",
  PQRSD_DESISTIMIENTO_DECREED: "error",
  PQRSD_APPEAL_FILED: "warning",
  PQRSD_APPEAL_GRANTED: "success",
  PQRSD_APPEAL_DENIED: "error",
  PQRSD_APPEAL_REMINDER_SENT: "warning",
  PQRSD_DESISTIMIENTO_FINAL: "error",
  RESPONSE_FILED_FINAL: "success",
  RESPONSE_FLOW_DONE: "success",
  PQRSD_CITIZEN_CLOSED: "success",
  PQRSD_AI_CLASSIFIED: "info",
  PQRSD_AI_ROUTED: "info",
  PQRSD_AI_NEEDS_REVIEW: "warning",
  // Not an error: an anonymous citizen chose not to leave an email.
  EMAIL_SKIPPED: "default",
};

// MUI palettes have no "default.main"; neutral events get a grey rule.
const borderColorFor = (color) => (!color || color === "default" ? "grey.400" : `${color}.main`);

// Keys rendered as additional context lines under an event (only when present).
const EXTRA_KEYS = [
  ["reason", "Motivo"],
  ["ratingLabel", "Calificación"],
  ["termRule", "Regla del plazo"],
  ["termNote", "Nota del plazo"],
  ["ratingScore", "Puntaje"],
  ["note", "Nota / motivo"],
  ["toUserNames", "Asignado a"],
  ["toUserName", "Hacia"],
  ["fromUserName", "Desde"],
  ["previousReturnReason", "Motivo de devolución previa"],
  ["responseType", "Tipo de respuesta"],
  ["responseFiledId", "Radicado respuesta"],
  ["responseIndex", "Respuesta #"],
  ["deadline", "Nuevo plazo"],
  ["trigger", "Origen"],
  ["infoRequestId", "Requerimiento"],
  ["requerimientoFiledId", "Radicado requerimiento"],
  ["dueAt", "Plazo del ciudadano"],
  ["lastDay", "Último día del recurso"],
  ["decisionDueAt", "Resolver a más tardar"],
  ["actoFiledId", "Radicado del acto"],
  ["decisionFiledId", "Radicado de la decisión"],
  ["decision", "Decisión"],
  ["itemCount", "Ítems solicitados"],
  ["documentName", "Documento"],
  ["flowId", "Flujo"],
  ["template", "Plantilla"],
  ["to", "Destinatario"],
  ["subject", "Asunto del correo"],
  ["groupName", "Dependencia"],
  ["priority", "Prioridad"],
  ["confidence", "Confianza IA"],
];

function formatTs(value) {
  if (!value) return "—";
  const d = value?.toDate ? value.toDate() : new Date(value);
  if (isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat("es-CO", {
    timeZone: "America/Bogota",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(d);
}

function eventTime(ev) {
  const raw = ev?.at || ev?.fecha || null;
  if (!raw) return 0;
  const d = raw?.toDate ? raw.toDate() : new Date(raw);
  const t = d.getTime();
  return isNaN(t) ? 0 : t;
}

export default function PqrsdLogsDrawer({ open, onClose, events }) {
  const sorted = useMemo(() => {
    const list = Array.isArray(events) ? events.slice() : [];
    return list.sort((a, b) => eventTime(b) - eventTime(a));
  }, [events]);

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: { xs: 320, sm: 420 }, p: 2 }} role="presentation">
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6">{pqrsdText("Logs de la PQRSD")}</Typography>
          <IconButton onClick={onClose} size="small" aria-label="Cerrar logs">
            <CloseIcon />
          </IconButton>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
          Bitácora completa de eventos del expediente (solo administradores y supervisores).
        </Typography>
        <Divider sx={{ mb: 2 }} />

        {sorted.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No hay eventos registrados todavía.
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {sorted.map((ev, idx) => {
              const type = ev.eventType || ev.tipoEvento || "EVENTO";
              const label = pqrsdText(EVENT_LABEL[type] || type);
              const title = describeEvent(ev, label);
              const actor = ev.byName || ev.porNombre || "Sistema";
              return (
                <Box
                  key={ev.eventId || `${type}-${idx}`}
                  sx={{
                    borderLeft: 3,
                    borderColor: borderColorFor(EVENT_COLOR[type] || "info"),
                    pl: 1.5,
                    py: 0.5,
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Chip size="small" label={label} color={EVENT_COLOR[type] || "default"} variant="outlined" />
                    <Typography variant="caption" color="text.secondary">
                      {formatTs(ev.at || ev.fecha)}
                    </Typography>
                  </Stack>
                  <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 600 }}>
                    {title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                    Por: {actor}
                  </Typography>
                  {EXTRA_KEYS.map(([key, lbl]) => {
                    const text = formatEventValue(key, ev[key]);
                    if (text === null) return null;
                    return (
                      <Typography
                        key={key}
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", whiteSpace: "pre-wrap" }}
                      >
                        {lbl}: {text}
                      </Typography>
                    );
                  })}
                  <Typography
                    variant="caption"
                    color="text.disabled"
                    sx={{ display: "block", mt: 0.25, fontFamily: "monospace", fontSize: 10 }}
                  >
                    {type}
                  </Typography>
                </Box>
              );
            })}
          </Stack>
        )}
      </Box>
    </Drawer>
  );
}
